import { Box, SxProps, Theme, styled } from '@mui/material';
import { Component, ReactNode } from 'react';
import { ObjectWithId } from '@synergy/models';
import { PaginatedResponse } from '@synergy/api';
import { WithStyles, createStyles, withStyles } from '@mui/styles';
import { useSearchParams } from 'react-router-dom';

import { OverviewTableEmptyResult } from './overview-table-empty.result';
import { OverviewTablePagination } from './overview-table-pagination';
import { PoolTable, PoolTableColumn, PoolTableRow } from '../table';
import { useFilter } from '../filter';

const styles = (theme: Theme) =>
    createStyles({
        root: {
            backgroundColor: '#FFF',
            '& > span': {
                display: 'block',
                marginBottom: theme.spacing(2),
            },

            '&.MuiPaper-root': {
                backgroundColor: '#FAFAFA',
            },
        },
        table: {
            backgroundColor: '#FFF',

            '& th': {
                color: theme.palette.grey[800],
            },
            // The column insets come from the theme — hardcoding them kept the
            // outer columns on the legacy inset wherever the theme had moved on.
            '& th:first-child': {
                paddingLeft: theme.general.tableGutter.outer,
            },
            '& td:first-child': {
                paddingLeft: theme.general.tableGutter.outer,
                width: '1%',
            },
            '& th:nth-child(2)': {
                paddingLeft: theme.general.tableGutter.inner,
            },
            '& td:nth-child(2)': {
                paddingLeft: theme.general.tableGutter.inner,
                width: '1%',
            },
            '& td:last-child': {
                paddingRight: theme.general.tableGutter.outer,
            },
            '&.has-additional-header thead th': {
                top: 56,
            },
        },
        selectable: {
            backgroundColor: '#FFF',
            '& tr > td:first-child': {
                paddingLeft: theme.general.tableGutter.outer,
            },

            '& tr > th:first-child': {
                paddingLeft: theme.general.tableGutter.outer,
            },
            '&.has-additional-header thead th': {
                top: 56,
            },
        },
    });

const Footer = styled(Box)(({ theme }) => ({
    bottom: 0,
    position: 'sticky',
    backgroundColor: theme.palette.background.paper,
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    borderTop: theme.tableFooter?.borderTop ?? '1px solid #6F7377',
    ...(theme.tableFooter?.background ? { backgroundColor: theme.tableFooter.background } : {}),
}));

export const OverviewTableContainer = styled(Box)(({ theme }) => ({
    // The side gutter comes from the theme — hardcoding it kept these tables
    // inset by the legacy page margin wherever the theme had moved on.
    margin: `${theme.spacing(2)} ${theme.general.pageContentSpacing} ${theme.spacing(4)}`,
    flex: 1,
    overflow: 'auto',
}));

interface OverviewTableBaseProps extends WithStyles<typeof styles> {
    data: PaginatedResponse<ObjectWithId>;
    columns: PoolTableColumn[];
    rows: PoolTableRow[];
    requesting?: boolean;
    stickyHeader?: boolean;
    selectable?: boolean;
    selected?: number[];
    selectionDisabled?: number[];
    enableHover?: boolean;
    additionalHeader?: ReactNode;
    onSelected?: (selected: number[]) => void;
    onItemSelected: (id: number) => void;
    onResetSearch?: () => void;
    // wrapper props
    searchParams: URLSearchParams;
    onUpdateSearchParams: (searchParams: URLSearchParams) => void;
    sx?: SxProps<Theme>;
    emptyResultMessage?: ReactNode;
    emptyResultAction?: ReactNode;
}

class OverviewTableBase extends Component<OverviewTableBaseProps> {
    private handleRowClicked = (row: PoolTableRow) => {
        const id = row.id;
        if (id && this.props.onItemSelected) {
            this.props.onItemSelected(id);
        }
    };

    private handleSort = (order: 'asc' | 'desc', sort: string) => {
        this.props.searchParams.set('sort', sort);
        this.props.searchParams.set('order', order);
        this.props.onUpdateSearchParams(this.props.searchParams);
    };

    public override render() {
        const { columns, rows, data, stickyHeader, classes, sx, additionalHeader } = this.props;

        if (data.total <= 0 && !this.props.requesting) {
            return (
                <OverviewTableEmptyResult
                    emptyResultMessage={this.props.emptyResultMessage}
                    emptyResultAction={this.props.emptyResultAction}
                    onReset={this.props.onResetSearch}
                />
            );
        }

        return (
            <OverviewTableContainer sx={sx}>
                {additionalHeader && (
                    <Box
                        sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 3,
                        }}
                    >
                        {additionalHeader}
                    </Box>
                )}
                <PoolTable
                    className={`${this.props.selectable ? classes.selectable : classes.table} ${
                        additionalHeader ? 'has-additional-header' : ''
                    }`}
                    columns={columns}
                    rows={rows}
                    onSort={this.handleSort}
                    onRowClicked={this.handleRowClicked}
                    stickyHeader={stickyHeader}
                    selectable={this.props.selectable}
                    selected={this.props.selected}
                    onSelected={this.props.onSelected}
                    selectionDisabled={this.props.selectionDisabled}
                    enableHover={this.props.enableHover}
                />
                <Footer>
                    <OverviewTablePagination
                        pages={data.pages || Math.ceil(data.total / data.limit)}
                        maxPage={data.maxPage}
                        total={data.total}
                    />
                </Footer>
            </OverviewTableContainer>
        );
    }
}

type OverviewTableProps = Omit<OverviewTableBaseProps, 'searchParams' | 'onUpdateSearchParams'>;

type OverviewTableWrapperProps = OverviewTableProps & WithStyles<typeof styles>;

const OverviewTableWrapper = (props: OverviewTableWrapperProps) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const filter = useFilter();

    return (
        <OverviewTableBase
            {...props}
            searchParams={searchParams}
            onUpdateSearchParams={setSearchParams}
            onResetSearch={props.onResetSearch ?? filter.reset}
        />
    );
};

export const OverviewTable = withStyles(styles)(OverviewTableWrapper);
